/**
 * @re/shared — constants and types shared by the backend and the three frontends.
 * Values here MUST stay in sync with the CHECK constraints / seed data in
 * backend/database/init/*.sql.
 */

// ---------------------------------------------------------------------------
// Permissions (seeded in `permissions`)
// ---------------------------------------------------------------------------
export const PERMISSIONS = [
  'dashboard.view',
  'properties.view.own',
  'properties.view.all',
  'properties.create',
  'properties.update.own',
  'properties.update.all',
  'properties.change_status',
  'properties.delete',
  'requests.view.own',
  'requests.view.all',
  'requests.create',
  'requests.update.own',
  'requests.update.all',
  'requests.assign',
  'requests.change_status',
  'requests.delete',
  'form.submit',
  'form.share',
  'users.view',
  'users.manage',
  'roles.manage',
  'org.settings',
  'data.export',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const SYSTEM_ROLES = ['OWNER', 'MANAGER', 'EDITOR', 'DATA_ENTRY', 'SALES'] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

export const EMPLOYEE_TYPES = ['OWNER', 'MANAGER', 'EDITOR', 'DATA_ENTRY', 'SALES'] as const;
export type EmployeeType = (typeof EMPLOYEE_TYPES)[number];

// ---------------------------------------------------------------------------
// Domain enums
// ---------------------------------------------------------------------------
export const DIRECTIONS = ['OFFER', 'DEMAND'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const SOURCES = ['INTERNAL', 'PUBLIC_LINK'] as const;
export type Source = (typeof SOURCES)[number];

export const ORG_STATUSES = ['ACTIVE', 'SUSPENDED', 'ARCHIVED'] as const;
export type OrgStatus = (typeof ORG_STATUSES)[number];

export const USER_STATUSES = ['ACTIVE', 'DISABLED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const PROPERTY_STATUSES = [
  'PENDING_REVIEW',
  'AVAILABLE',
  'RESERVED',
  'SOLD',
  'RENTED',
  'WITHDRAWN',
  'ARCHIVED',
] as const;
export type PropertyStatus = (typeof PROPERTY_STATUSES)[number];

export const REQUEST_STATUSES = [
  'NEW',
  'ASSIGNED',
  'IN_PROGRESS',
  'CONTACTED',
  'MATCHED',
  'WON',
  'LOST',
  'CANCELLED',
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** Request type codes seeded as system types (cannot be deleted). */
export const SYSTEM_REQUEST_TYPES = ['BUY', 'SALE', 'INVEST', 'RENT'] as const;

// ---------------------------------------------------------------------------
// API error envelope
// ---------------------------------------------------------------------------
export const ERROR_CODES = [
  'VALIDATION_ERROR',
  'FORM_VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'TOKEN_EXPIRED',
  'INVALID_CREDENTIALS',
  'ACCOUNT_LOCKED',
  'ACCOUNT_DISABLED',
  'PASSWORD_CHANGE_REQUIRED',
  'ORGANIZATION_UNAVAILABLE',
  'SUBSCRIPTION_EXPIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'VERSION_CONFLICT',
  'DUPLICATE',
  'INVALID_REFERENCE',
  'BUSINESS_RULE',
  'FORM_VERSION_EXPIRED',
  'PUBLIC_FORM_DISABLED',
  'CAPTCHA_FAILED',
  'RATE_LIMITED',
  'FILE_INVALID',
  'STORAGE_NOT_CONFIGURED',
  'INTERNAL_ERROR',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorDetail {
  field?: string;
  rule?: string;
  message?: string;
  [k: string]: unknown;
}

export interface ApiErrorBody {
  error: {
    code: ErrorCode | string;
    message: string;
    details?: ApiErrorDetail[];
  };
  requestId?: string;
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  total?: number;
}

/** Text shown to companies: Arabic is the source, English is required before publishing. */
export interface Localized {
  ar: string;
  en?: string;
}

// ---------------------------------------------------------------------------
// Organization branding (stored in organizations.settings JSONB)
// ---------------------------------------------------------------------------
/** Fixed social platforms — icons are built into each frontend, not uploaded URLs. */
export const ORG_SOCIAL_PLATFORMS = ['website', 'instagram', 'facebook', 'twitter', 'tiktok'] as const;
export type OrgSocialPlatform = (typeof ORG_SOCIAL_PLATFORMS)[number];

export const ORG_SOCIAL_PLATFORM_LABELS: Record<OrgSocialPlatform, string> = {
  website: 'الموقع',
  instagram: 'انستغرام',
  facebook: 'فيسبوك',
  twitter: 'تويتر (X)',
  tiktok: 'تيك توك',
};

export interface OrganizationSocialLink {
  platform: OrgSocialPlatform;
  url: string;
}

/** Single primary color for the org app / public surfaces (MUI theme primary.main). */
export interface OrganizationTheme {
  primaryColor: string | null;
}

export interface OrganizationBranding {
  theme: OrganizationTheme;
  socialLinks: OrganizationSocialLink[];
}
